import React, { useEffect, useMemo, useState } from "react";
import * as A from "./styled";
import { FaAngleRight, FaArrowLeft } from "react-icons/fa6";
import { FiBellOff } from "react-icons/fi";
import * as interFace from "../../Config/interface.config";
import { isCreatorRole } from "../../Config/common.config";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../Redux/store";
import { logout as LogOut } from "../../Redux/slices/authSlice";
import SignOutConfirmationModal from "../../Components/SignOutConfirmationModal";
import toast from "react-hot-toast";
import {
  clearCreatorNotificationsForSignOut,
  CreatorNotificationState,
  disableCreatorNotifications,
  enableCreatorNotifications,
  getCreatorNotificationState,
  prepareCreatorNotifications,
} from "../../Utils/pushNotifications";

const creatorLinks: interFace.LinkInterface[] = [
  {
    title: "Contact Support",
    url: "mailto:business@fanxo.club?subject=Fanxo Support",
  },
  {
    title: "Edit Profile",
    url: "/profile/edit",
  },
  {
    title: "Change Password",
    url: "/password/change",
  },
  {
    title: "Payouts",
    url: "/request-payouts",
  },
  {
    title: "Subscription",
    url: "/membership/pricing",
  },
  {
    title: "Sign Out",
    url: "/",
  },
];

const fanLinks: interFace.LinkInterface[] = [
  creatorLinks[0],
  creatorLinks[1],
  creatorLinks[2],
  {
    title: "Membership",
    url: "/user-membership",
  },
  creatorLinks[5],
];

const creatorNotificationCopy: Record<
  CreatorNotificationState,
  { label: string; description: string }
> = {
  enabled: {
    label: "Enabled",
    description: "This device will alert you for a new subscriber, gift or message.",
  },
  disabled: {
    label: "Off",
    description: "Enable alerts for a new subscriber, gift or message.",
  },
  blocked: {
    label: "Blocked",
    description: "Allow notifications for Fanxo in your browser or phone settings.",
  },
  unsupported: {
    label: "Not supported",
    description: "Install or open Fanxo in a browser that supports notifications.",
  },
};

const fanNotificationCopy: Record<
  CreatorNotificationState,
  { label: string; description: string }
> = {
  enabled: {
    label: "Enabled",
    description: "This device will alert you when a creator sends you a new message.",
  },
  disabled: {
    label: "Off",
    description: "Enable alerts when a creator sends you a new message.",
  },
  blocked: {
    label: "Blocked",
    description: "Allow notifications for Fanxo in your browser or phone settings.",
  },
  unsupported: {
    label: "Not supported",
    description: "Install or open Fanxo in a browser that supports notifications.",
  },
};
const Account: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [notificationState, setNotificationState] =
    useState<CreatorNotificationState>("disabled");
  const [notificationBusy, setNotificationBusy] = useState(false);
  const isCreator = isCreatorRole(user.role);
  const links = useMemo(() => (isCreator ? creatorLinks : fanLinks), [isCreator]);
  const notificationDetails = (isCreator ? creatorNotificationCopy : fanNotificationCopy)[notificationState];

  useEffect(() => {
    // Warm the service-worker registration and VAPID key without requesting
    // permission. The account's later button click can subscribe immediately.
    void prepareCreatorNotifications().catch(() => undefined);
    void getCreatorNotificationState()
      .then(setNotificationState)
      .catch(() => setNotificationState("disabled"));
  }, [isCreator]);

  const toggleCreatorNotifications = async () => {
    if (notificationBusy || notificationState === "unsupported") return;

    setNotificationBusy(true);
    try {
      const nextState =
        notificationState === "enabled"
          ? await disableCreatorNotifications()
          : await enableCreatorNotifications();

      setNotificationState(nextState);
      if (nextState === "enabled") {
        toast.success("Notifications enabled on this device");
      } else if (nextState === "blocked") {
        toast.error("Notifications are blocked in your browser settings");
      } else {
        toast.success("Notifications disabled on this device");
      }
    } catch (error: any) {
      toast.error(error?.message || "Unable to update notifications");
      const currentState = await getCreatorNotificationState().catch(
        () => "disabled" as CreatorNotificationState
      );
      setNotificationState(currentState);
    } finally {
      setNotificationBusy(false);
    }
  };

  const confirmSignOut = async () => {
    setShowSignOutConfirm(false);

    await clearCreatorNotificationsForSignOut().catch(() => undefined);

    dispatch(LogOut());
    navigate("/", { replace: true });
  };

  return (
    <A.Account>
      <h1 className="text-white font-noto flex items-center gap-5 md:gap-10">
        <span className="cursor-pointer" onClick={() => navigate("/home")}>
          <FaArrowLeft size={30} />
        </span>
        <span>Account</span>
      </h1>

      <A.AccountLinkWrapper>
        {notificationState !== "enabled" && (
          <A.NotificationCard>
            <A.NotificationIdentity>
              <A.NotificationIcon $enabled={false}>
                <FiBellOff size={21} />
              </A.NotificationIcon>
              <div>
                <A.NotificationTitle>
                  Notifications
                  <A.NotificationStatus $state={notificationState}>
                    {notificationDetails.label}
                  </A.NotificationStatus>
                </A.NotificationTitle>
                <A.NotificationDescription>
                  {notificationDetails.description}
                </A.NotificationDescription>
                <A.NotificationNote>
                  Receive alerts from Fanxo.
                </A.NotificationNote>
              </div>
            </A.NotificationIdentity>

            <A.NotificationButton
              type="button"
              onClick={toggleCreatorNotifications}
              disabled={
                notificationBusy ||
                notificationState === "unsupported" ||
                notificationState === "blocked"
              }
            >
              {notificationBusy
                ? "Updating..."
                : notificationState === "blocked"
                    ? "Open settings"
                    : "Enable"}
            </A.NotificationButton>
          </A.NotificationCard>
        )}

        {links.map((link, index) => (
          <A.AccountLinkBox
            onClick={(event) => {
              if (link.title === "Contact Support") {
                event.preventDefault();
                window.location.href = link.url;
                return;
              }
              if (link.title === "Sign Out") {
                event.preventDefault();
                setShowSignOutConfirm(true);
              }
            }}
            key={`account-link-${index}`}
            to={link.url}
          >
            <h3>{link.title}</h3>
            <FaAngleRight />
          </A.AccountLinkBox>
        ))}
      </A.AccountLinkWrapper>
      <SignOutConfirmationModal
        show={showSignOutConfirm}
        onStay={() => setShowSignOutConfirm(false)}
        onConfirm={confirmSignOut}
      />
    </A.Account>
  );
};

export default Account;
